import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {
  DatasetViewQuery,
  DatasetViewQueryVariables,
} from 'data-frames/workspace/__generated/DatasetViewQuery';
import {DATASET_VIEW_QUERY} from 'data-frames/workspace/DatasetView';
import {QueriedPersistedDataFrameTableWrapper} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface DatasetTableWrapperProps {
  id: string;
  actionOrigin: ActionOrigin;
  rowLimit?: number;
}

// this component displays the DataFrame associated with a Dataset
// it queries for a Dataset, then passes its DataFrame to a version of PersistedDataFrameTableWrapper
export default function DatasetTableWrapper({
  id,
  actionOrigin,
  rowLimit,
}: DatasetTableWrapperProps): JSX.Element | null {
  assert(
    BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS,
    'ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS is not enabled'
  );
  const result = useBenchlingQuery<DatasetViewQuery, DatasetViewQueryVariables>(DATASET_VIEW_QUERY, {
    variables: {id, limit: rowLimit ?? DEFAULT_DATA_FRAME_ROW_LIMIT},
  });
  const clientStateId = `dataset-table-${id}`;

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {data} = result;
    if (isInaccessible(data.dataset)) {
      return (
        <WorkspaceItemInaccessibleGuard
          inaccessibleInfo={data.dataset}
          modelType={ModelTypeConstants.DATASET}
        />
      );
    } else {
      const dataFrame = isInaccessible(data.dataset.dataFrame) ? null : data.dataset.dataFrame;
      return (
        <div className={css(styles.dataFrame)}>
          {dataFrame && (
            <QueriedPersistedDataFrameTableWrapper
              persistedDataFrame={dataFrame}
              actionOrigin={actionOrigin}
              clientStateId={clientStateId}
              defaultTableMode={DataFrameTableMode.RICH}
              renderIsExcludedColumn={false}
              isCollapsible={true}
            />
          )}
        </div>
      );
    }
  } else {
    return null;
  }
}

const styles = StyleSheet.create({
  dataFrame: {
    marginBottom: '16px',
    marginTop: '16px',
  },
});
